import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, Loader2, RefreshCw } from "lucide-react";
import { Navigate, useParams } from "react-router-dom";
import { getLibrary, getDocumentDownloadUrl, getDocumentVersionDownloadUrl, listDocuments } from "@/apps/rag/api/rag-api.js";
import { mapDocument, mapPublicLibrary } from "@/apps/rag/api/mappers.js";
import {
  DocumentViewerPanel,
  type ActiveDocumentView,
} from "@/apps/rag/components/DocumentViewerPanel.js";
import { RagDocumentDetailsSheet } from "@/apps/rag/components/RagDocumentDetailsSheet.js";
import { ListPagination } from "@/apps/rag/components/ListPagination.js";
import { RagDocumentsGrid } from "@/apps/rag/components/RagDocumentsGrid.js";
import {
  RagDocumentsListToolbar,
  type RagDocumentsViewMode,
} from "@/apps/rag/components/RagDocumentsListToolbar.js";
import { RagDocumentsTable } from "@/apps/rag/components/RagDocumentsTable.js";
import { RagPageHeader } from "@/apps/rag/components/RagPageHeader.js";
import { isPdfDocument } from "@/apps/rag/lib/document-open.js";
import type { RagDocument, RagLibrary } from "@/apps/rag/types.js";
import { Button } from "@/components/ui/button";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { pageActionButtonClassName, pageActionLabelClassName } from "@/lib/page-action-button";
import { regulatoryLibrariesPath } from "../paths.js";

export function PublicLibraryDocumentsPage() {
  const { libraryId = "" } = useParams();
  const [library, setLibrary] = useState<RagLibrary | null>(null);
  const [documents, setDocuments] = useState<RagDocument[]>([]);
  const [activeDocument, setActiveDocument] = useState<ActiveDocumentView | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [documentsView, setDocumentsView] = useLocalStorage<RagDocumentsViewMode>(
    "regulatory-library-documents-view",
    "cards",
  );
  const [detailsDocumentId, setDetailsDocumentId] = useState<string | null>(null);

  const detailsDocument = useMemo(
    () => (detailsDocumentId ? documents.find((doc) => doc.id === detailsDocumentId) ?? null : null),
    [detailsDocumentId, documents],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQ(searchQuery.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, libraryId]);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);
    try {
      const [{ library: libraryData }, listResult] = await Promise.all([
        getLibrary(libraryId),
        listDocuments(libraryId, {
          page,
          limit,
          q: debouncedQ || undefined,
        }),
      ]);

      const mapped = mapPublicLibrary(libraryData);
      if (!mapped.isPublic) {
        setError("Library not found");
        setLibrary(null);
        return;
      }

      setLibrary(mapped);
      setDocuments(listResult.documents.map((doc) => mapDocument(doc, libraryId)));
      setTotal(listResult.total);
      const nextTotalPages = Math.max(1, Math.ceil(listResult.total / limit));
      if (page > nextTotalPages) {
        setPage(nextTotalPages);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load documents");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [libraryId, page, limit, debouncedQ]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const handleFocus = () => {
      void load({ silent: true });
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [load]);

  const openDocument = useCallback(
    async (documentId: string, title?: string, documentVersionId?: string | null) => {
      const document = documents.find((doc) => doc.id === documentId);
      const displayTitle = title ?? document?.title ?? "Document";
      const versionId = documentVersionId ?? undefined;

      try {
        if (
          document?.type === "PDF" ||
          isPdfDocument(null, displayTitle)
        ) {
          setActiveDocument({
            documentId,
            documentVersionId: versionId,
            title: displayTitle,
          });
          return;
        }

        const { downloadUrl } = versionId
          ? await getDocumentVersionDownloadUrl(documentId, versionId)
          : await getDocumentDownloadUrl(documentId);
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
      } catch {
        // View unavailable — page has no dedicated error slot for actions
      }
    },
    [documents],
  );

  if (loading) {
    return (
      <section className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </section>
    );
  }

  if (error || !library) {
    return <Navigate to={regulatoryLibrariesPath()} replace />;
  }

  return (
    <section className="flex h-[calc(100dvh-3rem)] min-h-0 flex-1 flex-col overflow-hidden p-6">
      <RagPageHeader
        icon={FileText}
        title="Documents"
        subtitle={`${library.name} · Public library · ${total} documents`}
        mobileActions={[
          {
            id: "refresh",
            icon: RefreshCw,
            label: "Refresh",
            disabled: refreshing,
            onClick: () => void load({ silent: true }),
          },
        ]}
        action={
          <Button
            variant="outline"
            disabled={refreshing}
            onClick={() => void load({ silent: true })}
            title="Refresh"
            aria-label="Refresh"
            className={pageActionButtonClassName}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            <span className={pageActionLabelClassName}>Refresh</span>
          </Button>
        }
      />

      {total > 0 || debouncedQ ? (
        <RagDocumentsListToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          view={documentsView}
          onViewChange={setDocumentsView}
        />
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto">
      {documents.length === 0 ? (
        <p className="text-sm text-slate-400">
          {debouncedQ
            ? <>No documents match &ldquo;{debouncedQ}&rdquo;</>
            : "No documents in this library."}
        </p>
      ) : documentsView === "table" ? (
        <RagDocumentsTable
          documents={documents}
          onView={(documentId) => {
            void openDocument(documentId);
          }}
        />
      ) : (
        <RagDocumentsGrid
          documents={documents}
          onDetails={(documentId) => setDetailsDocumentId(documentId)}
          onView={(documentId) => {
            void openDocument(documentId);
          }}
        />
      )}
      </div>
      <ListPagination
        className="mt-auto shrink-0"
        page={page}
        limit={limit}
        total={total}
        loading={loading || refreshing}
        onPageChange={setPage}
        onLimitChange={(nextLimit) => {
          setLimit(nextLimit);
          setPage(1);
        }}
      />

      <RagDocumentDetailsSheet
        open={detailsDocumentId !== null}
        onOpenChange={(open) => {
          if (!open) setDetailsDocumentId(null);
        }}
        document={detailsDocument}
        workspaceName="Regulatory"
        libraryName={library.name}
        libraryId={libraryId}
        onView={(documentId) => {
          void openDocument(documentId);
        }}
        onViewChange={(documentId, title, documentVersionId) => {
          void openDocument(documentId, title, documentVersionId);
        }}
      />

      {activeDocument ? (
        <DocumentViewerPanel
          document={activeDocument}
          onClose={() => setActiveDocument(null)}
        />
      ) : null}
    </section>
  );
}
